import { ButtonBase, useTheme } from '@mui/material';
import { EmblaCarouselType } from 'embla-carousel';
import React, { ComponentPropsWithRef, useCallback, useEffect, useState } from 'react';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (_emblaApi: EmblaCarouselType) => void
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type PropType = ComponentPropsWithRef<'button'>;

export const PrevButton = (props: PropType) => {
  const { children, ...restProps } = props;
  const theme = useTheme();

  return (
    <ButtonBase
      data-component="PrevButton"
      className="embla__button embla__button--prev"
      disableRipple={true}
      {...restProps}
    >
      <svg
        width="44"
        height="44"
        viewBox="0 0 44 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="-1"
          y="1"
          width="41.0789"
          height="41.7783"
          rx="20.5395"
          transform="matrix(-1 0 0 1 41.0789 0)"
          stroke="white"
          strokeOpacity="0.5"
          strokeWidth="2"
        />
        <path
          d="M27.0393 13.5244L25.4033 11.8899L16.4926 20.7976C16.3489 20.9404 16.2349 21.1101 16.1571 21.297C16.0794 21.484 16.0393 21.6845 16.0393 21.887C16.0393 22.0895 16.0794 22.29 16.1571 22.4769C16.2349 22.6639 16.3489 22.8336 16.4926 22.9764L25.4033 31.8887L27.0378 30.2542L18.6744 21.8893L27.0393 13.5244Z"
          fill="url(#paint0_linear_2587_612)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_2587_612"
            x1="16.0393"
            y1="19.4895"
            x2="27.1312"
            y2="19.6339"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={theme.palette.brand.secondary} />
            <stop offset="1" stopColor={theme.palette.brand.secondary1} />
          </linearGradient>
        </defs>
      </svg>

      {children}
    </ButtonBase>
  );
};

export const NextButton = (props: PropType) => {
  const { children, ...restProps } = props;
  const theme = useTheme();

  return (
    <ButtonBase
      data-component="NextButton"
      className="embla__button embla__button--next"
      disableRipple={true}
      {...restProps}
    >
      <svg
        width="44"
        height="44"
        viewBox="0 0 44 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="1.07886"
          y="1"
          width="41.0789"
          height="41.7783"
          rx="20.5395"
          stroke="white"
          strokeOpacity="0.5"
          strokeWidth="2"
        />
        <path
          d="M16.1184 13.5244L17.7544 11.8899L26.6652 20.7976C26.8088 20.9404 26.9228 21.1101 27.0006 21.297C27.0784 21.484 27.1184 21.6845 27.1184 21.887C27.1184 22.0895 27.0784 22.29 27.0006 22.4769C26.9228 22.6639 26.8088 22.8336 26.6652 22.9764L17.7544 31.8887L16.12 30.2542L24.4833 21.8893L16.1184 13.5244Z"
          fill="url(#paint0_linear_2587_614)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_2587_614"
            x1="27.1184"
            y1="19.4895"
            x2="16.0266"
            y2="19.6339"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={theme.palette.brand.secondary} />
            <stop offset="1" stopColor={theme.palette.brand.secondary1} />
          </linearGradient>
        </defs>
      </svg>

      {children}
    </ButtonBase>
  );
};
